import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { CurrencyEur, Buildings, Printer, FileXls, CaretDown, CaretUp, EnvelopeSimple, DownloadSimple, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { openDownload } from "@/lib/downloadUrl";
import { useAuth, usePermissions } from "@/App";
import { useLoonZichtbaar, LoonSlot } from "@/components/LoonSlot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAAND_NAMEN = [
  "Januari", "Februari", "Maart", "April", "Mei", "Juni",
  "Juli", "Augustus", "September", "Oktober", "November", "December"
];

export default function Kosten() {
  const user = useAuth();
  const perms = usePermissions();
  const loonZichtbaar = useLoonZichtbaar();
  const canLonen = perms[user?.role || "medewerker"]?.lonen_zien === true && loonZichtbaar;
  const now = new Date();
  const [jaar, setJaar] = useState(String(now.getFullYear()));
  const [maand, setMaand] = useState(String(now.getMonth() + 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openFiliaal, setOpenFiliaal] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/kosten/maand`, { params: { jaar: parseInt(jaar), maand: parseInt(maand) } });
      setData(res.data);
    } catch (e) { toast.error("Fout bij ophalen kosten"); }
    finally { setLoading(false); }
  }, [jaar, maand]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleRapport = (fid) => setOpenFiliaal(openFiliaal === fid ? null : fid);

  const fmt = (val) => val.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleExportExcel = () => {
    if (!data) return;
    let csv = "\ufeff";
    const s = ";";
    csv += ["Filiaal", "Totaal Bedrag", "Totaal Uren", "Medewerkers"].join(s) + "\n";
    data.filialen.forEach((f) => {
      csv += [f.filiaal_naam, String(f.totaal_bedrag).replace(".", ","), String(f.totaal_uren).replace(".", ","), f.aantal_medewerkers].join(s) + "\n";
      f.medewerkers.forEach((m) => {
        csv += ["  " + m.naam, String(m.bedrag).replace(".", ","), String(m.uren).replace(".", ","), String(m.uurloon).replace(".", ",")].join(s) + "\n";
      });
    });
    csv += ["TOTAAL", String(data.totaal_alle).replace(".", ","), "", ""].join(s) + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `kosten_${MAAND_NAMEN[parseInt(maand)-1].toLowerCase()}_${jaar}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="kosten-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div>
          <h1 className="page-title">Kosten Overzicht</h1>
          <p className="text-sm text-muted-foreground mt-1">Totaal kosten personeel per filiaal per maand</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={jaar} onValueChange={setJaar}>
            <SelectTrigger className="rounded-sm w-24 h-9" data-testid="kosten-jaar"><SelectValue /></SelectTrigger>
            <SelectContent>{[2024, 2025, 2026, 2027].map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}</SelectContent>
          </Select>
          <Select value={maand} onValueChange={setMaand}>
            <SelectTrigger className="rounded-sm w-32 h-9" data-testid="kosten-maand"><SelectValue /></SelectTrigger>
            <SelectContent>{MAAND_NAMEN.map((n, i) => (<SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>))}</SelectContent>
          </Select>
          <Button onClick={handleExportExcel} size="sm" className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white h-9" data-testid="kosten-export-btn">
            <FileXls size={16} weight="bold" className="mr-1.5" /> Excel
          </Button>
          <Button onClick={() => window.print()} size="sm" variant="outline" className="rounded-sm h-9" data-testid="kosten-print-btn">
            <Printer size={16} weight="bold" className="mr-1.5" /> Print
          </Button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block print:mb-4">
        <h1 className="text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>Kosten Overzicht — {MAAND_NAMEN[parseInt(maand) - 1]} {jaar}</h1>
      </div>

      {loading ? (
        <div className="h-64 bg-muted animate-pulse rounded-sm" />
      ) : data ? (
        <div className="bg-white border border-[#002FA7]/20 rounded-sm overflow-hidden" data-testid="kosten-filiaal-table">
          {/* Table header */}
          <div className="bg-[#002FA7] text-white px-3 sm:px-5 py-3 flex items-center justify-between">
            <span className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Alle Filialen — {MAAND_NAMEN[parseInt(maand) - 1]} {jaar}</span>
            <span className="font-black text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>{"\u20AC"}{fmt(data.totaal_alle)}</span>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#002FA7]/5 border-b border-[#002FA7]/15">
                <th className="px-3 sm:px-5 py-2.5 text-left font-bold text-xs tracking-wider uppercase text-[#002FA7]">Filiaal</th>
                <th className="px-3 sm:px-5 py-2.5 text-right font-bold text-xs tracking-wider uppercase text-[#002FA7] w-36">Totaal Bedrag</th>
                <th className="px-3 sm:px-5 py-2.5 text-center font-bold text-xs tracking-wider uppercase text-[#002FA7] w-28">Rapport</th>
              </tr>
            </thead>
            <tbody>
              {data.filialen.map((f, idx) => (
                <Fragment key={f.filiaal_id}>
                  <tr className={`border-b border-[#002FA7]/8 hover:bg-[#002FA7]/3 transition-colors ${idx % 2 === 1 ? "bg-[#002FA7]/[0.02]" : ""}`} data-testid={`kosten-filiaal-row-${f.filiaal_id}`}>
                    <td className="px-3 sm:px-5 py-3 font-medium">
                      <div className="flex items-center gap-2.5">
                        <div className="w-2 h-2 rounded-full bg-[#002FA7]" />
                        {f.filiaal_naam}
                      </div>
                    </td>
                    <td className="px-3 sm:px-5 py-3 text-right font-bold tabular-nums">{"\u20AC"}{fmt(f.totaal_bedrag)}</td>
                    <td className="px-3 sm:px-5 py-3 text-center">
                      <button
                        onClick={() => toggleRapport(f.filiaal_id)}
                        className="inline-flex items-center gap-1 text-[#002FA7] hover:bg-[#002FA7]/10 font-semibold text-xs px-3 py-1.5 rounded-sm transition-colors"
                        data-testid={`rapport-btn-${f.filiaal_id}`}
                      >
                        Detail {openFiliaal === f.filiaal_id ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                      </button>
                    </td>
                  </tr>
                  {openFiliaal === f.filiaal_id && (
                    <tr data-testid={`rapport-detail-${f.filiaal_id}`}>
                      <td colSpan={3} className="p-0 border-b border-[#002FA7]/15">
                        <div className="bg-[#002FA7]/[0.03] px-3 sm:px-5 py-3 space-y-4">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-[#002FA7]/60">
                                <th className="pb-2 text-left font-bold uppercase tracking-wider">Medewerker</th>
                                <th className="pb-2 text-right font-bold uppercase tracking-wider w-20">Uren</th>
                                <th className="pb-2 text-right font-bold uppercase tracking-wider w-20">Uurloon</th>
                                <th className="pb-2 text-right font-bold uppercase tracking-wider w-24">Bedrag</th>
                              </tr>
                            </thead>
                            <tbody>
                              {f.medewerkers.length > 0 ? f.medewerkers.map((m, mi) => (
                                <tr key={mi} className="border-t border-[#002FA7]/8">
                                  <td className="py-2 font-medium">{m.naam}</td>
                                  <td className="py-2 text-right tabular-nums">{String(m.uren).replace(".", ",")}</td>
                                  <td className="py-2 text-right tabular-nums">{"\u20AC"}{String(m.uurloon).replace(".", ",")}</td>
                                  <td className="py-2 text-right font-semibold tabular-nums">{"\u20AC"}{fmt(m.bedrag)}</td>
                                </tr>
                              )) : (
                                <tr><td colSpan={4} className="py-3 text-center text-muted-foreground">Geen uren deze maand</td></tr>
                              )}
                              <tr className="border-t-2 border-[#002FA7]/20 font-bold">
                                <td className="py-2">Subtotaal</td>
                                <td className="py-2 text-right tabular-nums">{String(f.totaal_uren).replace(".", ",")}</td>
                                <td className="py-2"></td>
                                <td className="py-2 text-right tabular-nums">{"\u20AC"}{fmt(f.totaal_bedrag)}</td>
                              </tr>
                            </tbody>
                          </table>

                          <FiliaalAanwezigheid
                            filiaal={f}
                            jaar={jaar}
                            maand={maand}
                            canLonen={canLonen}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              <tr className="bg-[#002FA7] text-white font-black">
                <td className="px-3 sm:px-5 py-3 font-black">TOTAAL</td>
                <td className="px-3 sm:px-5 py-3 text-right font-black tabular-nums text-base">{"\u20AC"}{fmt(data.totaal_alle)}</td>
                <td className="px-3 sm:px-5 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
      {!canLonen && (
        <div className="bg-white border border-border rounded-sm p-8 text-center" data-testid="kosten-vergrendeld">
          <p className="text-sm font-semibold">Loonkosten staan op slot</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Geef je wachtwoord via de knop "Lonen verborgen" bovenaan om de kosten te tonen.
          </p>
          <div className="flex justify-center"><LoonSlot /></div>
        </div>
      )}
    </div>
  );
}

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FiliaalAanwezigheid({ filiaal, jaar, maand, canLonen }) {
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [mailOpen, setMailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let actief = true;
    setLaden(true);
    axios
      .get(`${API_URL}/maandoverzicht`, {
        params: { jaar: parseInt(jaar), maand: parseInt(maand), filiaal_id: filiaal.filiaal_id },
      })
      .then((res) => { if (actief) setData(res.data); })
      .catch(() => { if (actief) toast.error("Fout bij ophalen aanwezigheid"); })
      .finally(() => { if (actief) setLaden(false); });
    return () => { actief = false; };
  }, [jaar, maand, filiaal.filiaal_id]);

  const qs = `jaar=${jaar}&maand=${maand}&filiaal_id=${encodeURIComponent(filiaal.filiaal_id)}`;
  const exportExcel = () => openDownload(`${API_URL}/export/maandoverzicht-excel?${qs}`);
  const exportCsv = () => openDownload(`${API_URL}/export/maandoverzicht?${qs}`);

  const verstuur = async () => {
    if (!emailTo) { toast.error("Vul een email adres in"); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/email/rapport`, {
        email: emailTo, type: "maand", jaar: parseInt(jaar), maand: parseInt(maand),
        filiaal_id: filiaal.filiaal_id,
      });
      toast.success(`Rapport verstuurd naar ${emailTo}`);
      setMailOpen(false);
      setEmailTo("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij versturen");
    } finally { setSending(false); }
  };

  const rijen = (data?.rijen || []).filter((r) => (r.totaal_uren || 0) > 0);
  const dagen = data?.dagen || [];
  const fmtUren = (v) => (!v ? "" : String(v).replace(".", ","));

  return (
    <div className="border-t border-[#002FA7]/15 pt-3" data-testid={`aanwezigheid-blok-${filiaal.filiaal_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Clock size={14} weight="bold" className="text-[#002FA7]" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#002FA7]">
            Aanwezigheid {MAAND_NAMEN[parseInt(maand) - 1]} {jaar} — {filiaal.filiaal_naam}
          </span>
        </div>
        <div className="flex gap-1.5 print:hidden">
          <Button onClick={exportExcel} size="sm" variant="outline" className="rounded-sm h-7 text-[11px] px-2" data-testid={`aanw-excel-${filiaal.filiaal_id}`}>
            <FileXls size={13} weight="bold" className="mr-1" /> Excel
          </Button>
          <Button onClick={exportCsv} size="sm" variant="outline" className="rounded-sm h-7 text-[11px] px-2" data-testid={`aanw-csv-${filiaal.filiaal_id}`}>
            <DownloadSimple size={13} weight="bold" className="mr-1" /> CSV
          </Button>
          <Button onClick={() => window.print()} size="sm" variant="outline" className="rounded-sm h-7 text-[11px] px-2" data-testid={`aanw-print-${filiaal.filiaal_id}`}>
            <Printer size={13} weight="bold" className="mr-1" /> Print / PDF
          </Button>
          <Button onClick={() => setMailOpen(true)} size="sm" className="rounded-sm h-7 text-[11px] px-2 bg-[#002FA7] hover:bg-[#002280] text-white" data-testid={`aanw-mail-${filiaal.filiaal_id}`}>
            <EnvelopeSimple size={13} weight="bold" className="mr-1" /> Mailen
          </Button>
        </div>
      </div>

      {laden ? (
        <div className="h-24 bg-muted animate-pulse rounded-sm" />
      ) : rijen.length === 0 ? (
        <p className="text-[11px] text-muted-foreground py-3">Niemand aanwezig in dit filiaal deze maand</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#002FA7]/15 rounded-sm">
          <table className="text-[11px] border-collapse min-w-max">
            <thead>
              <tr className="bg-[#002FA7]/5">
                <th className="sticky left-0 z-10 bg-[#002FA7]/5 px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[#002FA7] min-w-[150px]">Medewerker</th>
                {dagen.map((d, i) => (
                  <th key={i} className={`px-1 py-1.5 text-center font-bold tabular-nums ${d.is_weekend ? "bg-[#002FA7]/10 text-[#002FA7]" : "text-[#002FA7]/70"}`}>
                    <span className="block text-[9px] font-semibold opacity-60">{(d.dag_naam || "").slice(0, 2)}</span>
                    {d.dag ?? d.dag_num ?? i + 1}
                  </th>
                ))}
                <th className="px-2 py-1.5 text-right font-bold uppercase tracking-wider text-[#002FA7] min-w-[60px]">Uren</th>
                {canLonen && <th className="px-2 py-1.5 text-right font-bold uppercase tracking-wider text-[#002FA7] min-w-[80px]">Loon</th>}
              </tr>
            </thead>
            <tbody>
              {rijen.map((r) => (
                <tr key={r.medewerker_id} className="border-t border-[#002FA7]/10" data-testid={`aanw-rij-${r.medewerker_id}`}>
                  <td className="sticky left-0 z-10 bg-white px-2 py-1.5 font-semibold whitespace-nowrap">{r.naam}</td>
                  {(r.uren_per_dag || []).map((u, i) => (
                    <td key={i} className={`px-1 py-1.5 text-center tabular-nums ${dagen[i]?.is_weekend ? "bg-[#002FA7]/[0.04]" : ""} ${u ? "font-semibold" : "text-muted-foreground"}`}>
                      {fmtUren(u)}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right font-bold tabular-nums">{fmtUren(r.totaal_uren)}</td>
                  {canLonen && <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{"\u20AC"}{(r.totaal_loon || 0).toFixed(2).replace(".", ",")}</td>}
                </tr>
              ))}
              <tr className="border-t-2 border-[#002FA7]/25 bg-[#002FA7]/5 font-bold">
                <td className="sticky left-0 z-10 bg-[#002FA7]/5 px-2 py-1.5 uppercase tracking-wider text-[#002FA7]">Totaal</td>
                {dagen.map((d, i) => {
                  const som = rijen.reduce((s, r) => s + (r.uren_per_dag?.[i] || 0), 0);
                  return (
                    <td key={i} className={`px-1 py-1.5 text-center tabular-nums ${d.is_weekend ? "bg-[#002FA7]/10" : ""}`}>
                      {som ? fmtUren(Math.round(som * 100) / 100) : ""}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-right tabular-nums" data-testid={`aanw-totaal-uren-${filiaal.filiaal_id}`}>
                  {fmtUren(Math.round(rijen.reduce((s, r) => s + (r.totaal_uren || 0), 0) * 100) / 100)}
                </td>
                {canLonen && (
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {"\u20AC"}{rijen.reduce((s, r) => s + (r.totaal_loon || 0), 0).toFixed(2).replace(".", ",")}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid={`aanw-mail-dialog-${filiaal.filiaal_id}`}>
          <DialogHeader>
            <DialogTitle>Rapport mailen — {filiaal.filiaal_naam}</DialogTitle>
            <DialogDescription>
              Verstuur het maandoverzicht van {MAAND_NAMEN[parseInt(maand) - 1]} {jaar} voor dit filiaal met Excel-bijlage.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-semibold">E-mailadres</Label>
            <Input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="naam@bedrijf.be"
              className="rounded-sm mt-1"
              data-testid={`aanw-mail-input-${filiaal.filiaal_id}`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setMailOpen(false)}>Annuleren</Button>
            <Button onClick={verstuur} disabled={sending} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid={`aanw-mail-verstuur-${filiaal.filiaal_id}`}>
              {sending ? "Versturen..." : "Versturen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
